import React, { useContext, useEffect } from "react";
import { format } from "date-fns";
import { toDate } from "./dateUtils";

import { useQuery } from "@apollo/client";
import { GET_AVAILABLE_TIME_SLOTS } from "../../queries";
import { AppointmentSchedulerContext } from "./AppointmentScheduler";
import { Spinner } from "@shopify/polaris";

function AppointmentTimePicker({ selectedDay, onContinuePressed }) {
  const { appointmentType, appointmentContactMethod, selectedTimeSlot, setSelectedTimeSlot } =
    useContext(AppointmentSchedulerContext);

  useEffect(() => {
    setSelectedTimeSlot(undefined);
  }, [selectedDay, setSelectedTimeSlot]);

  const { data, loading } = useQuery(GET_AVAILABLE_TIME_SLOTS, {
    variables: {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
      userIds: appointmentType.applicableCareTeamMemberIds,
      endDate: selectedDay?.toString(),
      startDate: selectedDay?.toString(),
      appointmentTypeId: appointmentType.id,
      contactMethod: appointmentContactMethod
    },
    skip: !selectedDay
  });

  const { morningTimes, afternoonTimes, eveningTimes } = categorizeSlots(data?.availableTimeSlots || []);

  function availableSlot(slot, index) {
    return (
      <div
        key={index}
        onClick={() => setSelectedTimeSlot(slot)}
        className={`available-slot ${selectedTimeSlot == slot ? "active-slot" : ""}`}
      >
        {format(toDate(slot.startTime), "h:mm a").toLowerCase()}
      </div>
    );
  }

  return (
    <div className="appointment-time-picker">
      <div className="embeddable-availability-triangle" />
      <div className="availability-on-header">
        <span className="availability-on-header-date">{format(selectedDay, "MMMM d, yyyy")}</span>
        <p className="availability-on-header-timezone">
          Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"}
        </p>
      </div>
      {loading ? (
        <div className="available-times-loading-container">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="areas-of-day-flexbox">
            {!!morningTimes.length && (
              <div className="day-area">
                <span className="day-area-header">Morning</span>
                <div className="available-slots-for-day">
                  {morningTimes.map((slot, index) => availableSlot(slot, index))}
                </div>
              </div>
            )}

            {!!afternoonTimes.length && (
              <div className="day-area">
                <span className="day-area-header">Afternoon</span>
                <div className="available-slots-for-day">
                  {afternoonTimes.map((slot, index) => availableSlot(slot, index))}
                </div>
              </div>
            )}

            {!!eveningTimes.length && (
              <div className="day-area">
                <span className="day-area-header">Evening</span>
                <div className="available-slots-for-day">
                  {eveningTimes.map((slot, index) => availableSlot(slot, index))}
                </div>
              </div>
            )}

            {morningTimes.length === 0 && afternoonTimes.length === 0 && eveningTimes.length === 0 ? (
              <div className="appointment-time-picker-empty-state">
                <p>No available time slots</p>
                <p>Please select a different date in the calendar.</p>
                <p>You can change the month by pressing the arrow at the top right of the calendar.</p>
              </div>
            ) : undefined}
          </div>

          <div className="available-slot-action">
            <button
              className="sw-button primary-button slot-confirm-button"
              disabled={!selectedTimeSlot}
              onClick={onContinuePressed}
            >
              Confirm Appointment
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function categorizeSlots(availableTimeSlots) {
  const morningTimes = [];
  const afternoonTimes = [];
  const eveningTimes = [];

  const times = {
    morningTimes,
    afternoonTimes,
    eveningTimes
  };

  if (availableTimeSlots == null) {
    return times;
  }

  availableTimeSlots.forEach(slot => {
    const date = toDate(slot.startTime);

    if (format(date, "a") === "AM" && parseInt(format(date, "H"), 10) < 12) {
      morningTimes.push(slot);
    } else if (format(date, "H") === "12" || format(date, "H") === "17" || parseInt(format(date, "H"), 10) < 17) {
      afternoonTimes.push(slot);
    } else {
      eveningTimes.push(slot);
    }
  });

  return times;
}

export default AppointmentTimePicker;
